/*
Usage Notes:

The following web component can be dropped into any standard Shopify theme (i.e. non-headless), using Liquid to pass product data:

  {% assign skioSellingPlanGroups = product.selling_plan_groups | where: 'app_id', 'SKIO' %}
  {% if skioSellingPlanGroups.size > 0 %}
    <skio-plan-picker
      product='{{ product | json | escape }}'
      selectedVariant='{{ product.selected_or_first_available_variant | json | escape }}'
      formId='{{ product_form_id }}'
      currency='{{ cart.currency.iso_code }}'
      >
    </skio-plan-picker>
    <input type="hidden" aria-hidden="true" name="selling_plan" value="">
    <script src="{{ 'skio-plan-picker-component.js' | asset_url }}" type="module"></script>
  {% endif %}

  Note: formId not required if element is inside of a form already

  Example variantChanged event dispatch:
  document.dispatchEvent( new CustomEvent("variantChanged", { detail: { variantId: variant.id } }) );
*/

import {
  LitElement,
  html,
  css,
} from "https://cdn.jsdelivr.net/gh/lit/dist@2/core/lit-core.min.js";

const skioStyles = css`
  .skio-plan-picker {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 0;
    border: 0;
    position: relative;
  }
  .skio-onetime-second {
    order: 2;
  }

  .skio-group-container {
    display: none;
  }
  .skio-group-container--available {
    display: block;
    position: relative;
    box-shadow: 0 0 5px rgba(23, 24, 24, 0.05), 0 1px 2px rgba(0, 0, 0, 0.07);
    border-radius: 5px;
    border-width: 1.5px;
    border-color: transparent;
    border-style: solid;
    transition: border-color 0.2s ease;
  }
  .skio-group-container--selected {
    border-color: #000;
    background-color: var(--isSelectedBg);
  }

  .skio-group-container--selected .selling-points {
    opacity: 1;
  }

  .skio-group-input {
    position: absolute;
    width: 0px;
    height: 0px;
    opacity: 0;
  }
  .skio-group-input:focus-visible ~ .skio-group-label {
    outline: 1.5px #ccc solid;
    outline-offset: 4px;
    border-radius: 5px;
  }

  .skio-group-label {
    display: flex;
    flex-direction: column;
    cursor: pointer;
    padding: 10px;
    overflow: hidden;
  }

  .skio-group-topline {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    font-size: 14px;
    font-weight: 500;
    flex-wrap: wrap;
  }

  .skio-radio__container {
    display: flex;
    align-items: center;
    margin-right: 5px;
    width: min-content;
    min-height: 0;
    line-height: 0;
  }

  .skio-radio {
    transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1),
      opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    transform-origin: center;
    transform: scale(0);
    opacity: 0;
  }
  .skio-group-label:hover .skio-radio {
    transform: scale(1);
    opacity: 0.75;
  }
  .skio-group-container--selected .skio-group-label .skio-radio {
    transform: scale(1);
    opacity: 1;
  }

  .skio-price {
    margin-left: auto;
    display: flex;
    gap: 0.25rem;
    font-size: 16px;
  }

  .skio-price .original-price {
    color: #8e8e8e;
  }

  .skio-group-content {
    width: auto;
    transition: max-height 0.25s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    max-height: 38px;
    margin-top: 6px;
    opacity: 1;
  }

  /* Hide frequency if not selected */
  .skio-group-container:not(.skio-group-container--selected)
    .skio-group-content {
    opacity: 0.5;
    pointer-events: none;
  }

  .skio-group-title {
    min-width: max-content;
    font-size: 16px;
  }

  .skio-save {
    color: #212121;
    border: 0.25px #000000 solid;
    padding: 0.25rem 0.5rem;
    border-radius: 20px;
    background-color: white;
    font-size: 0.9rem;
  }

  .skio-frequency {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 8px 30px 8px 10px;
    margin-top: 5px;
    border-radius: 5px;
    background-color: #fff;
    box-shadow: 0 0 0 1px #000;
    color: black;
    width: 100%;
    border: 0;
    font-size: 14px;
    font-family: inherit;
    white-space: nowrap;
    text-overflow: ellipsis;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7' /%3E%3C/svg%3E");
    background-position: right 10px top 50%;
    background-size: 18px;
    background-repeat: no-repeat;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
  }
  .skio-frequency.skio-frequency--one {
    background-image: none;
    pointer-events: none;
  }

  .skio-frequency span {
    text-transform: lowercase;
  }

  button.add-to-cart {
    justify-content: center;
    font-weight: 400;
    text-transform: capitalize;
    letter-spacing: 0;
    font-style: normal;
    font-size: 16px;
    text-decoration: none;
    box-shadow: 0 0 #0000004d inset;
    width: 100%;
    color: #fff;
    background-color: #000;
    border-radius: 5px;
    transition-duration: 0.3s;
    padding: 10px 32px;
    text-transform: uppercase;
    cursor: pointer;
    font-family: "FK Grotesk Neue Trial";
    transition: border-color 0.25s ease, background-color 0.25s ease,
      color 0.25s ease;

    border: solid 2px rgba(0, 0, 0, 0);
  }
  button.add-to-cart:hover {
    background-color: rgba(0, 0, 0, 0);
    color: #000;
    border-color: #000;
  }

  .skio-details {
    --text-color: #333;
    --text-color-secondary: #888;

    user-select: none;
    -webkit-user-select: none;
    order: 3;
    position: relative;
  }

  .skio-details summary::-webkit-details-marker,
  .skio-details summary::marker,
  .skio-details slot {
    color: rgba(0, 0, 0, 0) !important;
  }

  .skio-details summary {
    margin-top: 30px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    outline: none;
  }

  .skio-details summary:focus,
  .skio-details summary:focus-visible {
    outline: none;
  }

  .skio-details summary span.skio-details-label {
    font-size: 12px;
    display: flex;
    padding: 0.5em;
    cursor: pointer;
    align-items: center;
    gap: 4px;
    text-decoration: underline;
    margin-top: -30px;
    color: #000;
    outline: none;
  }

  @media (max-width: 1300px) {
    .skio-details summary {
      flex-direction: column;
      align-items: flex-start;
      gap: 0;
      margin-top: 10px;
    }
    .skio-details summary span.skio-details-label {
      margin-top: -20px;
    }
  }

  @keyframes fadeInDown {
    0% {
      opacity: 0;
      transform: translateY(-15px);
    }
    100% {
      opacity: 1;
      transform: translateY(0px);
    }
  }
  .skio-details[open] > .skio-details--content {
    animation-name: fadeInDown;
    animation-duration: 0.3s;
  }

  .skio-details--content {
    position: absolute;
    z-index: 1020;
    padding: 8px 12px;
    font-size: 12px;
    width: fit-content;
    max-width: 275px;
    background: #fff0f4;
    border: 1px solid #dedede;
    box-shadow: 0 0 5px rgb(23 24 24 / 5%), 0 1px 2px rgb(0 0 0 / 7%);
  }

  .skio-details--headline {
    font-weight: 700;
    margin-top: 0;
    margin-bottom: 4px;
  }

  .selling-points {
    font-size: 14px;
    opacity: 0.5;
    list-style: none;
    padding-left: 0;
    margin-left: 0;
    color: black;
  }

  .selling-points li {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .selling-points li img {
    align-self: flex-start;
    padding-top: 0.4ch;
  }

  .skio-title-container {
    display: flex;
    justify-content: space-between;
    flex: 1 1 auto;
  }

  .skio-faint {
    opacity: 0.7;
  }

  .select-wrapper {
    position: relative;
    width: 100%;
  }

  .select-options {
    display: none;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: auto;
    z-index: 5;
    flex-direction: column;
    max-height: 250px;
    margin: 0;
    overflow: auto;
    border-radius: 5px;
    background-color: #fff;
    box-shadow: 0 0 0 1px #000;
    list-style: none;
    padding: 0;
  }

  .select-options li {
    font-size: 14px;
    padding: 0.4rem 0.8rem;
    cursor: pointer;
  }

  .select-options li[aria-selected] {
    order: -1;
  }

  .select-options-arrow {
    display: none;
    position: absolute;
    z-index: 6;
    width: 20px;
    height: 20px;
    top: 5px;
    right: 8px;
    transform: scaleY(-1);
    pointer-events: none;
  }

  .select-wrapper.open .select-options {
    display: flex;
  }

  .select-wrapper.open .select-options-arrow {
    display: block;
  }
`;

export class SkioPlanPickerComponent extends LitElement {
  static properties = {
    product: { type: Object }, //required
    productHandle: { type: String }, //optional (unless product isn't passed, then required)
    key: { type: String }, //optional, defaults to product.id; identifier for this instance of the Skio plan picker

    formId: { type: String }, //optional; if passed, used to connect input fields to form
    needsFormId: { type: Boolean }, //optional, defaults to false; if true, element needs to be passed a formId, else it searches for a form

    subscriptionFirst: { type: Boolean }, //optional, defaults to false; if true, shows subscription option above onetime
    startSubscription: { type: Boolean }, //optional, defaults to false; if true, auto-selects subscription on page load
    discountFormat: { type: String }, //optional, defaults to percent; can also pass "fixed"

    currency: { type: String }, //optional, defaults to 'USD', but can pass any 3 char identifier
    language: { type: String }, //optional, defaults to 'en-US', but can pass any similarly formatted language identifier
    moneyFormatter: {}, //placeholder for object

    externalPriceSelector: { type: String }, //optional, used to update the external price

    externalPriceSelectorWithCurrency: { type: String }, //optional, used to update the external price

    selectedVariant: { type: Object }, //placeholder for data
    skioSellingPlanGroups: {}, //placeholder for data
    availableSellingPlanGroups: {}, //placeholder for data
    selectedSellingPlanGroup: {}, //placeholder for data
    selectedSellingPlan: {}, //placeholder for data

    defaultFrequency: {}, //placeholder for data

    showAddToCartButton: { type: Boolean },

    meta: {},

    useVariantInputClickEvents: { type: Boolean }, // optional, allows use of variant input click events to update skio's selectedVariant
    variantInputSelector: {},
  };

  static styles = skioStyles;

  constructor() {
    super();
    this.product = null;
    this.selectedVariant = null;

    this.productHandle = null;

    this.purchaseOption = "onetime";

    this.key = null;
    this.formId = null;
    this.needsFormId = false;

    this.skioSellingPlanGroups = [];
    this.availableSellingPlanGroups = [];

    this.selectedSellingPlanGroup = null;
    this.selectedSellingPlan = null;

    this.startSubscription = true;
    this.subscriptionFirst = false;

    this.skioMainProduct = true;

    this.discountFormat = "percent";

    this.externalPriceSelector = "[skio-external-price]";

    this.externalPriceSelectorWithCurrency =
      "[skio-external-price-with-currency]";

    this.currency = Shopify.currency.active;
    this.language = "en-US";
    this.moneyFormatter = new Intl.NumberFormat(this.language, {
      style: "currency",
      currency: this.currency,
      trailingZeroDisplay: "stripIfInteger",
    });

    this.defaultFrequency = null;

    this.showAddToCartButton = false;

    this.meta = "";

    this.lastSellingPlanName = "";

    this.showDetailsHover = false;

    this.useVariantInputClickEvents = null;
    this.variantInputSelector = null;

    this.pageLoad = true;
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.startSubscription == true) {
      this.purchaseOption = "subscription";
    }

    console.log("this.product", this.product);
    console.log("this.formId", this.formId);

    if (!this.product && this.productHandle) {
      this.fetchProduct(this.productHandle);
    }

    if (this.needsFormId && this.formId == null) {
      let forms = document.querySelectorAll('form[action="/cart/add"]');
      if (forms.length > 0) {
        let form;
        forms.forEach((el) => {
          if (el.hasAttribute("skio-key")) {
            if (el.getAttribute("skio-key") == this.key) form = el;
          }
        });
        if (!form) form = forms[0];
        this.formId = form.id;
        this.requestUpdate();
      }
    }
    let skio = this;
    document.addEventListener("variant:change:skio", function (e) {
      //update variant id
      let variantId = e.detail.variantId;
      let variant = skio.product.variants.find((x) => x.id == variantId);
      if (variant) skio.selectedVariant = variant;
      else skio.log("Unable to find variant with id: ", variantId);
      skio.requestUpdate();
    });

    this.moneyFormatter = new Intl.NumberFormat(this.language, {
      style: "currency",
      currency: skio.currency,
    });

    if (this.meta !== "") this.meta = JSON.parse(this.meta);

    if (this.useVariantInputClickEvents) {
      document.addEventListener("load", skio.addVariantClickEventListeners);
    }
  }

  render() {
    if (
      !this.product ||
      !this.selectedVariant ||
      this.skioSellingPlanGroups.length == 0 ||
      !this.product?.available
    )
      return;

    const { compare_at_price, price } = this.selectedVariant;
    const bagCount = this.selectedVariant.title.toLowerCase().includes("bag")
      ? parseInt(this.selectedVariant.title.match(/\d+/)?.[0] || "1")
      : 1;

    return html` <fieldset class="skio-plan-picker" skio-plan-picker="${
      this.key
    }">
      <input
        form="${this.formId}"
        name="selling_plan"
        type="hidden"
        value="${
          this.selectedSellingPlan !== null ? this.selectedSellingPlan?.id : ""
        }"
      />

      <div
        class="skio-group-container ${
          this.product.requires_selling_plan == false
            ? "skio-group-container--available"
            : ""
        } ${
      this.selectedSellingPlanGroup == null
        ? "skio-group-container--selected"
        : ""
    } ${this.subscriptionFirst ? "skio-onetime-second" : ""}"
        skio-group-container
        @click=${() => this.selectSellingPlanGroup(null)}
      >
        <input
          id="skio-one-time-${this.key}"
          class="skio-group-input"
          name="skio-group-${this.key}"
          type="radio"
          value=""
          skio-one-time
          ?checked=${
            this.startSubscription == false &&
            this.product.requires_selling_plan == false
              ? true
              : false
          }
        />
        <label
          data-one-time
          class="skio-group-label"
          for="skio-one-time-${this.key}"
          @click=${() => {
            let e = new CustomEvent("subscribeChange", {
              bubbles: true,
              detail: {
                onSubscribe: false,
              },
            });
            this.dispatchEvent(e);
          }}
          >
          <div class="skio-group-topline">
            <div class="skio-radio__container">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"></circle>
                <circle class="skio-radio" cx="12" cy="12" r="7" fill="currentColor"></circle>
              </svg>
            </div>
            <div class="skio-group-title">One-time Purchase</div>
            <div class="skio-price">
              ${
                Number(compare_at_price) > Number(price)
                  ? html`<s class="original-price skio-faint"
                      >${this.moneyFormatter
                        .format(compare_at_price / bagCount / 100)
                        .replace(".00", "")}</s
                    >`
                  : ""
              }
              <b skio-onetime-price>
                ${this.moneyFormatter
                  .format(price / bagCount / 100)
                  .replace(".00", "")}
              </b>
              </b><span class="skio-faint">/bag</span>
            </div>
          </div>
        </label>
      </div>
      ${
        this.availableSellingPlanGroups
          ? this.availableSellingPlanGroups.map(
              (group, index) =>
                html`
                  <div
                    class="skio-group-container skio-group-container--available ${this
                      .selectedSellingPlanGroup == group
                      ? "skio-group-container--selected"
                      : ""}"
                    skio-group-container
                    @click=${() => this.selectSellingPlanGroup(group)}
                  >
                    <input
                      id="skio-selling-plan-group-${index}-${this.key}"
                      class="skio-group-input"
                      name="skio-group-${this.key}"
                      type="radio"
                      value="${group.id}"
                      skio-selling-plan-group="${group.id}"
                      ?checked=${this.selectedSellingPlanGroup == group
                        ? true
                        : false}
                    />
                    <label
                      @click=${() => {
                        let e = new CustomEvent("subscribeChange", {
                          bubbles: true,
                          detail: {
                            onSubscribe: true,
                          },
                        });
                        this.dispatchEvent(e);
                      }}
                      data-selling-plan
                      class="skio-group-label"
                      for="skio-selling-plan-group-${index}-${this.key}"
                    >
                      <div class="skio-group-topline">
                        <div class="skio-radio__container">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              stroke-width="2"
                            ></circle>
                            <circle
                              class="skio-radio"
                              cx="12"
                              cy="12"
                              r="7"
                              fill="currentColor"
                            ></circle>
                          </svg>
                        </div>
                        <div class="skio-title-container">
                          <div class="skio-group-title">
                            ${group.name == "Subscription"
                              ? "Subscribe & Save"
                              : group.name}
                          </div>
                          <div class="skio-price">
                            ${this.maxPrice() >
                            this.maxPrice() -
                              this.discount(group.selected_selling_plan).amount
                              ? html`
                                  <del class="original-price skio-faint"
                                    >${this.moneyFormatter
                                      .format(this.maxPrice() / bagCount / 100)
                                      .replace(".00", "")}<del> </del
                                  ></del>
                                `
                              : html``}
                            <span skio-subscription-price>
                              <b>
                                ${this.money(
                                  this.price(
                                    group.selected_selling_plan,
                                    false
                                  ) / bagCount
                                ).replace(".00", "")}
                              </b>
                              <span class="skio-faint">/bag</span>
                            </span>
                          </div>
                        </div>
                      </div>
                      <div class="skio-group-content">
                        <div class="select-wrapper">
                          <select
                            skio-selling-plans="${group.id}"
                            class="skio-frequency${group.selling_plans.length ==
                            1
                              ? " skio-frequency--one"
                              : ""}"
                            @mousedown=${(e) => {
                              e.preventDefault();

                              e.currentTarget
                                .closest(".select-wrapper")
                                .classList.add("open");

                              document.addEventListener(
                                "mousedown",
                                this.toggleDropdown
                              );
                            }}
                            @change=${(e) =>
                              this.selectSellingPlan(e.target, group)}
                          >
                            ${group
                              ? group.selling_plans.map(
                                  (selling_plan) =>
                                    html`
                                      <option
                                        value="${selling_plan.id}"
                                        ?selected=${group.selected_selling_plan ==
                                        selling_plan}
                                      >
                                        ${selling_plan.name}
                                      </option>
                                    `
                                )
                              : ""}
                          </select>

                          <ul class="select-options">
                            ${group
                              ? group.selling_plans.map(
                                  (selling_plan) =>
                                    html`
                                      <li
                                        value="${selling_plan.id}"
                                        ?aria-selected=${group.selected_selling_plan ==
                                        selling_plan}
                                        @click=${(e) => {
                                          const select =
                                            e.currentTarget.parentElement
                                              .previousElementSibling;
                                          select.value =
                                            e.currentTarget.getAttribute(
                                              "value"
                                            );

                                          e.currentTarget.parentElement
                                            .querySelector(
                                              "li[aria-selected]"
                                            )
                                            ?.removeAttribute("aria-selected");

                                          e.currentTarget.setAttribute(
                                            "aria-selected",
                                            ""
                                          );

                                          e.currentTarget
                                            .closest(".select-wrapper")
                                            .classList.remove("open");

                                          document.removeEventListener(
                                            "mousedown",
                                            this.toggleDropdown
                                          );

                                          this.selectSellingPlan(select, group);
                                        }}
                                      >
                                        ${selling_plan.name}
                                      </li>
                                    `
                                )
                              : ""}
                          </ul>

                          <svg
                            class="select-options-arrow"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            stroke-width="2"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                      </div>
                      <ul class="selling-points">
                        <li>
                          <img
                            width="15"
                            height="15"
                            src="https://cdn.shopify.com/s/files/1/0270/9484/3435/files/heart-icon.svg?v=1709324582"
                            loading="lazy"
                          />
                          <span>Discount applied for life</span>
                        </li>
                        <li>
                          <img
                            width="15"
                            height="15"
                            src="https://cdn.shopify.com/s/files/1/0270/9484/3435/files/heart-icon.svg?v=1709324582"
                            loading="lazy"
                          />
                          <span
                            >No surprises! Pre-shipment & billing
                            reminders</span
                          >
                        </li>
                        <li>
                          <img
                            width="15"
                            height="15"
                            src="https://cdn.shopify.com/s/files/1/0270/9484/3435/files/heart-icon.svg?v=1709324582"
                            loading="lazy"
                          />
                          <span>Pause, swap or cancel anytime!</span>
                        </li>
                      </ul>
                    </label>
                  </div>
                `
            )
          : ""
      }

      <details
        class="skio-details"
        @mouseover=${(e) => this.detailsMouseover()}
        @mouseleave=${(e) => this.detailsMouseleave()}
      >
        <summary>
          <span class="skio-details-label">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.8445 5.15557L13.5111 5.51113L13.8667 2.93335"
                stroke="#111111"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M13.5112 5.51111C12.5334 3.2 10.2223 1.51111 7.55564 1.51111C4.17786 1.51111 1.33342 4.17777 1.15564 7.46666"
                stroke="#111111"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M2.9334 13.2444C3.32614 13.2444 3.64451 12.9261 3.64451 12.5333C3.64451 12.1406 3.32614 11.8222 2.9334 11.8222C2.54067 11.8222 2.22229 12.1406 2.22229 12.5333C2.22229 12.9261 2.54067 13.2444 2.9334 13.2444Z"
                fill="#111111"
              />
              <path
                d="M7.46673 15.1111C7.85946 15.1111 8.17784 14.7928 8.17784 14.4C8.17784 14.0073 7.85946 13.6889 7.46673 13.6889C7.07399 13.6889 6.75562 14.0073 6.75562 14.4C6.75562 14.7928 7.07399 15.1111 7.46673 15.1111Z"
                fill="#111111"
              />
              <path
                d="M12.0889 13.2444C12.4817 13.2444 12.8 12.9261 12.8 12.5333C12.8 12.1406 12.4817 11.8222 12.0889 11.8222C11.6962 11.8222 11.3778 12.1406 11.3778 12.5333C11.3778 12.9261 11.6962 13.2444 12.0889 13.2444Z"
                fill="#111111"
              />
              <path
                d="M13.9556 8.7111C14.3484 8.7111 14.6667 8.39273 14.6667 7.99999C14.6667 7.60725 14.3484 7.28888 13.9556 7.28888C13.5629 7.28888 13.2445 7.60725 13.2445 7.99999C13.2445 8.39273 13.5629 8.7111 13.9556 8.7111Z"
                fill="#111111"
              />
              <path
                d="M1.60003 11.2C1.99276 11.2 2.31114 10.8816 2.31114 10.4889C2.31114 10.0961 1.99276 9.77777 1.60003 9.77777C1.20729 9.77777 0.888916 10.0961 0.888916 10.4889C0.888916 10.8816 1.20729 11.2 1.60003 11.2Z"
                fill="#111111"
              />
              <path
                d="M5.0667 14.6667C5.45944 14.6667 5.77781 14.3483 5.77781 13.9556C5.77781 13.5628 5.45944 13.2444 5.0667 13.2444C4.67397 13.2444 4.35559 13.5628 4.35559 13.9556C4.35559 14.3483 4.67397 14.6667 5.0667 14.6667Z"
                fill="#111111"
              />
              <path
                d="M9.95562 14.6667C10.3484 14.6667 10.6667 14.3483 10.6667 13.9556C10.6667 13.5628 10.3484 13.2444 9.95562 13.2444C9.56288 13.2444 9.24451 13.5628 9.24451 13.9556C9.24451 14.3483 9.56288 14.6667 9.95562 14.6667Z"
                fill="#111111"
              />
              <path
                d="M13.4223 11.2C13.815 11.2 14.1334 10.8816 14.1334 10.4889C14.1334 10.0961 13.815 9.77777 13.4223 9.77777C13.0296 9.77777 12.7112 10.0961 12.7112 10.4889C12.7112 10.8816 13.0296 11.2 13.4223 11.2Z"
                fill="#111111"
              />
            </svg>

            Subscription details
          </span>
        </summary>
        <div class="skio-details--content">
          <p class="skio-details--headline">It’s Your Plan. You’re In Full Control!</p>
          You can adjust, delay or cancel at anytime. No Hassle Guarantee - You are always notified
          before each shipment.
        </div>
      </details>

      ${
        this.showAddToCartButton
          ? html`
              <button @click=${() => this.addToCart()} class="add-to-cart">
                Add to Cart
              </button>
            `
          : html``
      }
    </fieldset>`;
  }

  updated = (changed) => {
    if (changed.has("product") && this.product) {
      //update key
      this.key = this.key ? this.key : this.product.id;

      //update skioSellingPlanGroups
      this.skioSellingPlanGroups = this.product.selling_plan_groups.filter(
        (selling_plan_group) => selling_plan_group.app_id === "SKIO"
      );

      this.skioSellingPlanGroups.forEach((group) => {
        group.selling_plans.sort(function (a, b) {
          if (
            parseInt(a.name.replace(/\D/g, "")) <
            parseInt(b.name.replace(/\D/g, ""))
          )
            return -1;
          if (
            parseInt(a.name.replace(/\D/g, "")) >
            parseInt(b.name.replace(/\D/g, ""))
          )
            return 1;
          if (
            parseInt(a.name.replace(/\D/g, "")) ==
            parseInt(b.name.replace(/\D/g, ""))
          )
            return 0;
        });
      });
    }
    if (changed.has("selectedVariant") && this.selectedVariant) {
      //update availableSellingPlanGroups based on skioSellingPlanGroups and selectedVariant.id
      let skioSellingPlanGroups = JSON.parse(
        JSON.stringify(this.skioSellingPlanGroups)
      );
      this.availableSellingPlanGroups = skioSellingPlanGroups.filter(
        (selling_plan_group) =>
          selling_plan_group.selling_plans.some((selling_plan) =>
            this.selectedVariant.selling_plan_allocations.some(
              (selling_plan_allocation) =>
                selling_plan_allocation.selling_plan_id === selling_plan.id
            )
          )
      );

      //update selectedSellingPlan value
      if (this.availableSellingPlanGroups?.length > 0) {
        //update each group with a default selected_selling_plan

        this.availableSellingPlanGroups.forEach((group) => {
          group.selling_plans = group.selling_plans.filter((x) =>
            this.selectedVariant.selling_plan_allocations.find(
              (y) => y.selling_plan_id == x.id
            )
          );
        });

        this.availableSellingPlanGroups.forEach((group) => {
          if (this.defaultFrequency) {
            let selling_plan = group.selling_plans.find((x) =>
              x.name.toLowerCase().includes(this.defaultFrequency.toLowerCase())
            );
            if (selling_plan) group.selected_selling_plan = selling_plan;
            else group.selected_selling_plan = group.selling_plans[0];
          } else {
            group.selected_selling_plan = group.selling_plans[0];
          }
        });

        if (
          this.startSubscription == true ||
          this.product.requires_selling_plan == true ||
          this.purchaseOption == "subscription"
        ) {
          //find a matching selling plan, or choose first available

          if (
            this.selectedSellingPlan == null ||
            this.selectedSellingPlan == undefined
          ) {
            if(this.pageLoad === true){
              this.selectSellingPlanGroup(this.availableSellingPlanGroups[0]);
              this.pageLoad = false;
            }
            else {
              this.selectSellingPlanGroup();
            }
          }

          const sellingPlanName = this.selectedSellingPlan?.name;

          const sellingPlanGroup = this.availableSellingPlanGroups.find((x) =>
            x.selling_plans.find((y) => y.name == sellingPlanName)
          );
          const sellingPlan = sellingPlanGroup?.selling_plans.find(
            (y) => y.name == sellingPlanName
          );
          if (sellingPlan && sellingPlanName == sellingPlan.name) {
            this.selectedSellingPlanGroup = sellingPlanGroup;
            this.selectedSellingPlan = sellingPlan;
          }
        } else {
          this.selectedSellingPlan, (this.selectedSellingPlanGroup = null);
        }
      } else {
        this.selectedSellingPlan, (this.selectedSellingPlanGroup = null);
      }

      //update the form that was passed, if any
      this.updateForm();
    }

    if (changed.has("selectedSellingPlan")) {
      //update price of price elements if applicable
      document
        .querySelectorAll(`[skio-price][skio-key="${this.key}"]`)
        .forEach((el) => {
          el.innerHTML = this.price(this.selectedSellingPlan);
        });

      //update display of external content elements
      document
        .querySelectorAll(`[skio-onetime-content][skio-key="${this.key}"]`)
        .forEach((el) => {
          this.selectedSellingPlan !== null
            ? (el.style.display = "none")
            : el.style.removeProperty("display");
        });

      document
        .querySelectorAll(`[skio-subscription-content][skio-key="${this.key}"]`)
        .forEach((el) => {
          this.selectedSellingPlan == null
            ? (el.style.display = "none")
            : el.style.removeProperty("display");
        });
      //dispatch CustomEvent to tell that this specific plan picker was updated, and pass the selectedSellingPlan
      const event = new CustomEvent(`skio::update-selling-plan`, {
        bubbles: true,
        composed: true,
        detail: {
          sellingPlan: this.selectedSellingPlan,
          key: this.key,
        },
      });

      this.dispatchEvent(event);

      //update the form that was passed, if any
      this.updateExternalDiscounts();

      //update external selling_plan input value
      // let sellingPlanInput = document.querySelector('input[name="selling_plan"]')
      // this.selectedSellingPlan != null ? sellingPlanInput.value = this.selectedSellingPlan.id : sellingPlanInput.value = ''
    }
    this.updateForm();
    this.updateExternalPrice();
    if (changed.has("formId")) {
      //update the form that was passed, if any
      this.updateForm();
    }
  };

  log = (...args) => {
    args.unshift("%c[skio]", "color: #8770f2;");
    console.log.apply(console, args);
  };

  error = (...args) => {
    args.unshift("%c [skio]", "color: #ff0000");
    console.error.apply(console, args);
  };

  detailsMouseover() {
    let details = this.renderRoot.querySelector(".skio-details");
    let summary = this.renderRoot.querySelector(".skio-details summary");
    if (!details.hasAttribute("open") && this.showDetailsHover == false) {
      summary.click();
      this.showDetailsHover = true;
    }
  }

  detailsMouseleave() {
    let details = this.renderRoot.querySelector(".skio-details");
    let summary = this.renderRoot.querySelector(".skio-details summary");
    if (details.hasAttribute("open") && this.showDetailsHover == true) {
      summary.click();
      this.showDetailsHover = false;
    }
  }

  updatePriceElement() {
    let priceEl = document.querySelector(
      `[data-skio-price="${this.product.id}"]`
    );
    if (priceEl) {
      //add formatted price here
      let high = 0;
      this.product.variants.forEach((variant) => {
        if (variant.price > high) high = variant.price;
      });
      let content = "";
      if (!this.selectedSellingPlan) {
        content = this.moneyFormatter
          .format(this.selectedVariant.price / 100)
          .replaceAll("A", "")
          .replace(".00", "");
      } else {
        content = `<del>${this.moneyFormatter
          .format(high / 100)
          .replaceAll("A", "")
          .replace(".00", "")}</del>${this.price(this.selectedSellingPlan)
          .replaceAll("A", "")
          .replace(".00", "")}`;
      }
      priceEl.innerHTML = content;
    }
  }

  getMetadata(field) {
    let meta = this.meta;
    if (meta !== {}) {
      let fieldData = meta[field];
      if (fieldData) return fieldData;
      else return "";
    }
  }

  getVariantMetadata(variantId, field) {
    let meta = this.meta;
    if (meta !== {}) {
      let variantData = meta[variantId];
      if (variantData) {
        let fieldData = variantData[field];
        if (fieldData) return fieldData;
        else return "";
      }
    }
  }

  updateExternalPrice() {
    document.querySelectorAll(this.externalPriceSelector).forEach((el) => {
      this.selectedSellingPlan
        ? (el.innerHTML = this.price(this.selectedSellingPlan))
        : (el.innerHTML = this.money(this.selectedVariant.price));
    });
    document
      .querySelectorAll(this.externalPriceSelectorWithCurrency)
      .forEach((el) => {
        this.selectedSellingPlan
          ? (el.innerHTML =
              this.price(this.selectedSellingPlan) + " " + this.currency)
          : (el.innerHTML =
              this.money(this.selectedVariant.price) + " " + this.currency);
      });
  }

  updateExternalDiscounts() {
    console.log("updateExternalDiscounts", this.selectedSellingPlan);
    document
      .querySelectorAll("[data-skio-external-discount-onetime]")
      .forEach((el) => {
        el.style.display = this.selectedSellingPlan ? "none" : "";
      });
    document
      .querySelectorAll("[data-skio-external-discount-subscription]")
      .forEach((el) => {
        el.style.display = this.selectedSellingPlan ? "" : "none";
      });
  }

  // Update selected selling plan group; called on click of skio-group-container element
  selectSellingPlanGroup(group) {
    this.selectedSellingPlanGroup = group;
    this.selectedSellingPlan = group?.selected_selling_plan;
    if (this.selectedSellingPlan)
      this.lastSellingPlanName = this.selectedSellingPlan.name;
    if (group) this.purchaseOption = "subscription";
    else this.purchaseOption = "onetime";
    //update the form that was passed, if any
    this.updateForm();
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlan(element, group) {
    let selling_plan = group.selling_plans.find((x) => x.id == element.value);
    if (selling_plan) {
      group.selected_selling_plan = selling_plan;
      this.selectedSellingPlanGroup = group;
      this.selectedSellingPlan = selling_plan;
      this.lastSellingPlanName = this.selectedSellingPlan.name;
    } else {
      this.log(
        "Error: couldn't find selling plan with id " +
          element.value +
          " for variant " +
          this.selectedVariant.id +
          " from product " +
          this.product.id +
          " : " +
          this.product.handle
      );
    }
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlanButton(plan, group) {
    let selling_plan = group.selling_plans.find((x) => x.id == plan.id);
    if (selling_plan) {
      group.selected_selling_plan = selling_plan;
      this.selectedSellingPlanGroup = group;
      this.selectedSellingPlan = selling_plan;
      this.lastSellingPlanName = this.selectedSellingPlan.name;
    } else
      this.log(
        "Error: couldn't find selling plan with id " +
          element.value +
          " for variant " +
          this.selectedVariant.id +
          " from product " +
          this.product.id +
          " : " +
          this.product.handle
      );
  }

  // Formats integer value into money value
  money(price) {
    return this.moneyFormatter.format(price / 100.0).replace(".00", "");
  }

  maxPrice() {
    return Math.max(
      this.selectedVariant.price,
      this.selectedVariant.compare_at_price || 0
    );
  }

  // Calculates discount based on selling_plan.price_adjustments, returns { percent, amount } of selling plan discount
  discount(selling_plan) {
    if (!selling_plan) return { percent: "0%", amount: 0 };

    if (selling_plan.name.includes("prepaid")) {
      let option = selling_plan.options[0]["value"];

      var numberPattern = /\d+/g;
      let result = parseInt(option.match(numberPattern).join(""));

      const price_adjustment = selling_plan.price_adjustments[0];
      const discount = { percent: "0%", amount: 0 };
      const price = this.selectedVariant.price * result;

      switch (price_adjustment.value_type) {
        case "percentage":
          discount.percent = `${price_adjustment.value}%`;
          discount.amount = Math.round(
            (price * price_adjustment.value) / 100.0
          );
          break;
        case "fixed_amount":
          discount.percent = `${Math.round(
            ((price_adjustment.value * 1.0) / price) * 100.0
          )}%`;
          discount.amount = price - price_adjustment.value;
          break;
        case "price":
          discount.percent = `${Math.round(
            (((price - price_adjustment.value) * 1.0) / price) * 100.0
          )}%`;
          discount.amount = price - price_adjustment.value;
          break;
      }

      return discount;
    } else {
      const price_adjustment = selling_plan.price_adjustments[0];
      const discount = { percent: "0%", amount: 0 };
      const price = this.maxPrice();

      switch (price_adjustment.value_type) {
        case "percentage":
          discount.percent = `${price_adjustment.value}%`;
          discount.amount = Math.round(
            (price * price_adjustment.value) / 100.0
          );
          break;
        case "fixed_amount":
          discount.percent = `${Math.round(
            ((price_adjustment.value * 1.0) / price) * 100.0
          )}%`;
          discount.amount = price_adjustment.value;
          break;
        case "price":
          discount.percent = `${Math.round(
            (((price - price_adjustment.value) * 1.0) / price) * 100.0
          )}%`;
          discount.amount = price - price_adjustment.value;
          break;
      }

      return discount;
    }
  }

  // Calculates the variant's price for the given selling plan, returns a formatted money value (if desired)
  price(selling_plan, formatted = true) {
    const price = this.maxPrice();

    if (selling_plan.name.includes("prepaid")) {
      let option = selling_plan.options[0]["value"];
      var numberPattern = /\d+/g;
      let result = parseInt(option.match(numberPattern).join(""));

      return formatted
        ? this.money(price * result - this.discount(selling_plan).amount)
        : price * result - this.discount(selling_plan).amount;
    } else {
      return formatted
        ? this.money(price - this.discount(selling_plan).amount)
        : price - this.discount(selling_plan).amount;
    }
  }

  // If a formId was passed, appends the necessary <input> elements to the form
  updateForm() {
    if (this.formId) {
      let form = document.querySelector(`#${this.formId}`);

      if (form) {
        let selling_plan_input = form.querySelector('[name="selling_plan"]');
        if (selling_plan_input) {
          selling_plan_input.value =
            this.selectedSellingPlan?.id !== undefined
              ? this.selectedSellingPlan?.id
              : null;
          selling_plan_input.disabled =
            this.selectedSellingPlan?.id !== undefined ? false : true;
        } else {
          selling_plan_input = document.createElement("input");
          selling_plan_input.type = "hidden";
          selling_plan_input.name = "selling_plan";
          selling_plan_input.value =
            this.selectedSellingPlan?.id !== undefined
              ? this.selectedSellingPlan?.id
              : null;
          selling_plan_input.disabled =
            this.selectedSellingPlan?.id !== undefined ? false : true;
          form.append(selling_plan_input);
        }
      } else {
        console.log(
          `Skio error: form ID is ${this.formId}, but no form with that ID was found.`
        );
      }
    }
  }

  addVariantClickEventListeners() {
    let variantInputs = document.querySelectorAll(this.variantInputSelector);
    let skio = this;
    for (let el of variantInputs) {
      el.addEventListener("click", function (e) {
        // may need to replace with ID / e.target depending on client setup
        // may need to use different attribute depending on ^^
        let variantTitle = e.currentTarget.value;
        skio.selectedVariant = skio.product.variants.find(
          (variant) => variant.title == variantTitle
        );
      });
    }
  }

  // Optional functions keep if necessary

  /**
   *
   *
   */

  // Runs a fetch request to add the selectedVariant to the cart with the passed quantity and selectedSellingPlan
  addToCart(quantity = 1) {
    const items = [
      {
        id: this.selectedVariant.id,
        quantity: quantity,
        ...(this.selectedSellingPlan && {
          selling_plan: this.selectedSellingPlan?.id,
        }),
      },
    ];

    fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ items }),
    })
      .then((response) => response.json())
      .then((response) => {
        this.log("SKIO added item to cart: ", response);
        //dispatch CustomEvent to tell document that an item was added to cart
        const event = new CustomEvent(`skio::added-to-cart`, {
          bubbles: true,
          composed: true,
          detail: {
            response,
            key: this.key,
          },
        });

        this.dispatchEvent(event);
      })
      .catch((error) => {
        this.error(`SKIO ${this.key} error adding item to cart: `, error);
      });
  }

  fetchProduct = (handle) => {
    return fetch(`/products/${handle}.js`)
      .then((response) => response.json())
      .then((product) => {
        this.product = product;
        this.selectedVariant = product.variants[0];

        return product;
      });
  };

  toggleDropdown = (e) => {
    const isInsideShadowDOM = e
      .composedPath()
      .some((node) => node instanceof SkioPlanPickerComponent);

    const skio = document.querySelector("skio-plan-picker");
    const dropDown = skio.shadowRoot.querySelector(".select-wrapper");

    if (!isInsideShadowDOM && dropDown.classList.contains("open")) {
      dropDown.classList.remove("open");
      document.removeEventListener("mousedown", this.toggleDropdown);
    }
  };
}

customElements.define("skio-plan-picker", SkioPlanPickerComponent);